<template>
  <SelectControl
    v-if="!isDesktop"
    class="w-full block"
    :selected="selected"
    :options="options"
    :dusk="dusk"
    :size="size"
    :label="label"
    :bordered="bordered"
    @change="handleChange"
  >
    <slot />
  </SelectControl>

  <div v-else-if="useInlineOptions" class="panel-filter-select-options">
    <button
      v-for="option in listOptions"
      :key="String(option.value)"
      type="button"
      class="panel-dropdown-menu-item panel-dropdown-menu-item--lg w-full text-start"
      :class="{
        'panel-filter-select-option--active': option.value == selected,
        'panel-filter-select-option--disabled': option.disabled,
      }"
      :dusk="optionDusk(option)"
      :disabled="option.disabled === true"
      @click="handleChange(option.value)"
    >
      {{ labelFor(option) }}
    </button>
  </div>

  <Dropdown
    v-else
    ref="selectDropdown"
    class="w-full"
    :placement="isRtl ? 'bottom-end' : 'bottom-start'"
    :should-close-on-blur="false"
  >
    <Button
      variant="ghost"
      trailing-icon="chevron-down"
      padding="tight"
      class="panel-filter-select-trigger w-full justify-between"
      :dusk="dusk"
    >
      {{ selectedLabel }}
    </Button>

    <template #menu>
      <DropdownMenu width="240" class="panel-row-action-menu panel-filter-select-menu">
        <ScrollWrap :height="280">
          <div class="panel-row-action-menu-items">
            <DropdownMenuItem
              v-for="option in listOptions"
              :key="String(option.value)"
              as="button"
              size="large"
              :class="{
                'panel-filter-select-option--active': option.value == selected,
              }"
              :dusk="optionDusk(option)"
              :disabled="option.disabled === true"
              @click="selectOption(option.value)"
            >
              {{ labelFor(option) }}
            </DropdownMenuItem>
          </div>
        </ScrollWrap>
      </DropdownMenu>
    </template>
  </Dropdown>
</template>

<script>
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { Button } from 'laravel-nova-ui'

const INLINE_OPTION_LIMIT = 5

export default {
  components: {
    Button,
  },

  emits: ['change'],

  props: {
    selected: {},
    options: {
      type: Array,
      default: () => [],
    },
    dusk: {
      type: String,
      default: null,
    },
    size: {
      type: String,
      default: 'sm',
    },
    label: {
      default: 'label',
    },
    bordered: {
      type: Boolean,
      default: true,
    },
    includeEmptyOption: {
      type: Boolean,
      default: true,
    },
    emptyOptionLabel: {
      type: String,
      default: null,
    },
  },

  setup() {
    const breakpoints = useBreakpoints(breakpointsTailwind)

    return {
      isDesktop: breakpoints.greaterOrEqual('md'),
      isRtl: Nova.config('rtlEnabled'),
    }
  },

  methods: {
    labelFor(option) {
      return this.label instanceof Function
        ? this.label(option)
        : option[this.label]
    },

    optionDusk(option) {
      if (!this.dusk) {
        return undefined
      }

      return `${this.dusk}-option-${option.value}`
    },

    handleChange(value) {
      this.$emit('change', value)
    },

    selectOption(value) {
      this.handleChange(value)
      this.$refs.selectDropdown?.close?.()
    },
  },

  computed: {
    listOptions() {
      const options = [...this.options]

      if (!this.includeEmptyOption) {
        return options
      }

      const emptyLabel = this.emptyOptionLabel ?? this.__('—')
      const hasEmpty = options.some(option => option.value == '')

      if (!hasEmpty) {
        options.unshift({ value: '', label: emptyLabel })
      }

      return options
    },

    useInlineOptions() {
      return this.listOptions.length <= INLINE_OPTION_LIMIT
    },

    selectedLabel() {
      const selected = this.listOptions.find(option => option.value == this.selected)

      if (selected) {
        return this.labelFor(selected)
      }

      return this.emptyOptionLabel ?? this.__('—')
    },
  },
}
</script>
